<template>
<div class="chapter">
<div class="inner_page">
<h2 class="recphd" id="c03_sec1"><a id="page25"></a><span href="#/OEBPS/toc#c03_sec1" title="Link target outside of Sample">Grilled “Watermelon Garden” Salad <em>with</em> Lime Mango Dressing <em>and</em> Cornbread Croutons</span></h2>
<p class="graphic"><img alt="images" src="OEBPS/images/26-1.jpg" style="height:60vh"/></p>
<p class="serv"><em><strong>6 to 8 servings</strong></em></p>
<p class="serv1"><strong>PREP TIME</strong></p>
<p class="serv1"><em>20 minutes</em></p>
<p class="serv1"><strong>COOKING TIME</strong></p>
<p class="serv1"><em>15 minutes</em></p>
<p class="serv1"><strong>TOTAL TIME</strong></p>
<p class="serv1"><em>35 minutes</em></p>
<p class="nont">3 pounds seedless watermelon, peel left on, cut into 1-inch-thick slabs</p>
<p class="non">olive oil</p>
<p class="non">kosher salt</p>
<p class="non">½ cup hulled pumpkin seeds</p>
<p class="non">¼ teaspoon ground cayenne</p>
<p class="non">1 teaspoon sugar</p>
<p class="non">4 cups 1-inch cubes cornbread (½ of recipe <a href="#/OEBPS/c03_sec1#page28">here</a>, or purchased)</p>
<p class="non">2 tablespoons unsalted butter, melted</p>
<p class="non">8 fresh shiso leaves, shredded (or Thai basil or mint)</p>
<p class="non">½ cup dates, pitted and chopped</p>
<p class="non">1 serrano chile, sliced into rings</p>
<p class="non">¼ cup <a href="#/OEBPS/c03_sec1#page27">Lime Mango Dressing</a></p>
<p class="non">freshly ground black pepper</p>
<p class="nonb">½ cup ricotta or mascarpone</p>
<p class="extract">Few things say Harlem in the summertime like fresh watermelon. In 1935, when African-American businessman George Jones opened his classic movie theater, the Harlem, he didn’t just serve the usual fare of soft drinks, candy, and popcorn. In between his home (which was next door) and the theater, he opened what he called a “Watermelon Garden” with picnic tables and umbrellas where patrons could gather and socialize before and after the films.</p>
<p class="indent">If you’ve never grilled watermelon in the summertime, then you’re in for a treat. Grilling caramelizes the sweetness of the fruit, and the char gives just a hint of smoky flavor. The wood flavor of the grill will come through the watermelon, but it won’t be overpowering like the charred tomato or charred vegetables.</p>
<p class="indent">Just make sure to slice your watermelon thick enough that it doesn’t fall apart while you’re turning it on the grill.</p>
<p class="indent">Use some stale cornbread from the day before to add texture. It’ll be familiar yet wholly different from anything you’re used to.</p>
<p class="indent">Heat a grill pan to medium-high heat. Drizzle the watermelon slices with just enough oil to thinly coat and place them on the hot grill.</p>
<p class="indent"><a id="page26"></a>Grill each side of the watermelon slices for about 2 minutes, until charred. Transfer to a plate and season with salt.</p>
<p class="indent">Remove the rind and dice the grilled watermelon into large cubes.</p>
<p class="indent">Preheat the oven to 350°F. Toss the pumpkin seeds with the cayenne, sugar, and 2 teaspoons oil and place on a small baking sheet. Roast for 6 to 10 minutes, until golden.</p>
<p class="indent">Brush the cornbread cubes with the melted butter and place in a single layer on a baking sheet. Put in the oven along with the pumpkin seeds and bake for 10 minutes, turning halfway with a flexible spatula. Remove from the oven when the croutons are toasted and golden.</p>
<p class="indent">Toss the watermelon, shiso, dates, and chile in the bowl with the dressing. Taste and adjust the seasoning with salt and pepper. Add the cornbread croutons and toss very gently to combine without breaking the croutons. Spoon the salad onto a large platter and garnish with the pumpkin seeds and cheese.</p>
<h2 class="recphd1" id="c03_secc1"><a id="page27"></a>LIME MANGO DRESSING</h2>
<p class="indent">The lime in this dressing provides a sharper tang than lemon would.</p>
<p class="serv2"><em>Makes 1 cup</em></p>
<p class="nont1">½ cup coarsely chopped ripe mango</p>
<p class="non1">¼ cup fresh lime juice</p>
<p class="non1">¼ cup dry sake</p>
<p class="non1">2 tablespoons champagne vinegar</p>
<p class="non1">1 teaspoon minced seeded bird’s-eye chile</p>
<p class="non1">½ teaspoon kosher salt</p>
<p class="nonb1">¼ cup canola oil</p>
<p class="indent">Put all of the ingredients except the oil in a blender. Blend on high. After the ingredients are blended, slowly add the oil and continue to blend until the mixture is thickened and emulsified.</p>
<h2 class="recphd1" id="c03_secc2"><a id="page28"></a><a id="page29"></a>FRESH BAKED CORNBREAD</h2>
<p class="graphic"><img alt="images" src="OEBPS/images/29-1.jpg" style="height:60vh"/></p>
<p class="serv1"><em>Makes one</em></p>
<p class="serv1"><em>8-inch square or</em></p>
<p class="serv1"><em>9-inch round</em></p>
<p class="nont1">4 tablespoons unsalted butter, melted</p>
<p class="non1">1 cup milk</p>
<p class="non1">1 large egg</p>
<p class="non1">1¼ cups yellow cornmeal</p>
<p class="non1">1 cup all-purpose flour</p>
<p class="non1">½ cup sugar</p>
<p class="non1">1 tablespoon baking powder</p>
<p class="non1">½ teaspoon Aleppo pepper (or you can use ancho chile powder)</p>
<p class="non1">½ teaspoon salt</p>
<p class="nonb1">1 cup grated white cheddar cheese</p>
<p class="indent">Preheat the oven to 400°F. Brush the bottom and sides of an 8-inch square or 9-inch round cake pan with some of the melted butter.</p>
<p class="indent">In a large bowl, beat the remaining melted butter, milk, and egg with a fork or wire whisk until well mixed. In a separate bowl, combine the cornmeal, flour, sugar, baking powder, Aleppo pepper, and salt. Stir into the egg mixture and blend until the flour is just moistened (the batter will be lumpy). Gently fold in the cheese and set the batter aside to rest for 5 minutes.</p>
<p class="indent">Pour the batter into the prepared pan; use a rubber spatula to scrape batter from the bowl. Spread the batter evenly in the pan. Bake for 25 minutes, or until it is golden brown and a toothpick inserted in the center comes out clean.</p>
</div>
</div>
</template>
<script>

export default {
  name: 'chapter',
  data() {
    return {}
    },
      components: {
    }
  }
</script>



// WEBPACK FOOTER //
// c03_sec1.vue?8dd90a32